/**
 * GetStartedPage Component
 *
 * Pre-Auth0 warm-up page between homepage CTAs and Auth0 login.
 * Reinforces value proposition, sets expectations, and provides
 * a smoother transition to signup/login. Redirects authenticated
 * users to the dashboard.
 */

import { Navigate, Link } from "react-router-dom";
import {
  HeartHandshake,
  Shield,
  ArrowRight,
  UserPlus,
  ClipboardList,
  Upload,
  Lock,
  Check,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthTransition } from "../components/AuthTransition";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { useAuth } from "../hooks/useAuth";

export function GetStartedPage() {
  const { isAuthenticated } = useAuthStatus();
  const { login, signUp, isAuthenticating } = useAuth();

  // Authenticated users go to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {isAuthenticating && <AuthTransition message="Signing you in..." />}
      {/* Navigation */}
      <nav className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold text-foreground">
              AttuneWell
            </span>
          </Link>
          <Button variant="ghost" onClick={() => login()}>
            Sign In
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-2xl mx-auto space-y-12">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              Start Your Free Trial
            </h1>
            <p className="text-lg text-muted-foreground">
              Get AI-powered insights into your relationship communication
            </p>
          </div>

          {/* What you'll get */}
          <div className="rounded-xl border border-border bg-card p-6 md:p-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Here's what you'll get with your free trial:
            </h2>
            <ul className="space-y-3">
              {[
                "3 conversation analyses with AI-powered insights",
                "Science-backed therapy framework analysis (EFT, CBT, Gottman & more)",
                "Relationship tracker with AI coaching",
                "Fully encrypted — your data stays private",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What to expect */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-foreground text-center">
              What to expect
            </h2>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex flex-col items-center text-center p-4 rounded-lg bg-muted/50">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <UserPlus className="w-6 h-6 text-primary" />
                </div>
                <div className="text-sm font-medium text-foreground mb-1">
                  Create your account
                </div>
                <div className="text-xs text-muted-foreground">30 seconds</div>
              </div>
              <div className="flex flex-col items-center text-center p-4 rounded-lg bg-muted/50">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <ClipboardList className="w-6 h-6 text-primary" />
                </div>
                <div className="text-sm font-medium text-foreground mb-1">
                  Tell us about yourself
                </div>
                <div className="text-xs text-muted-foreground">1 minute</div>
              </div>
              <div className="flex flex-col items-center text-center p-4 rounded-lg bg-muted/50">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Upload className="w-6 h-6 text-primary" />
                </div>
                <div className="text-sm font-medium text-foreground mb-1">
                  Upload & get insights
                </div>
                <div className="text-xs text-muted-foreground">
                  Your first analysis
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center space-y-4">
            <Button
              size="lg"
              onClick={() => signUp()}
              className="gap-2 w-full sm:w-auto"
            >
              Create Your Free Account
              <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <button
                onClick={() => login()}
                className="text-primary hover:underline font-medium"
              >
                Sign In
              </button>
            </p>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground border-t border-border pt-8">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span>256-bit encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <HeartHandshake className="w-4 h-4 text-primary" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
